import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { LscAuthGate } from '../hooks/useLscAuthGate';
import { useLeague } from '../hooks/useLeague';
import {
  getLscVodSyncReadiness,
  listLscArchive,
  listLscMatches,
  listLscMembers,
  listLscVodCandidates,
} from '../api';
import LoadingState from '../components/LoadingState';
import ErrorBanner from '../components/ErrorBanner';
import CommissionerOnlyGate from '../components/CommissionerOnlyGate';
import CommissionerCockpit from '../components/leagueHome/CommissionerCockpit';
import type { LscVodCandidate } from '../types';
import { getDashboardRenderState } from '../leagueDashboardState';
import { lscReturnTo } from '../routing';
import {
  formatOverviewMemberDetail,
  getOverviewMemberCount,
} from '../utils/memberFilter';
import { formatDashboardVodSyncDetail } from '../syncReadiness';

const LscCommandCenterPage: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  return (
    <LscAuthGate returnTo={lscReturnTo(`/leagues/${leagueId}/command-center`)}>
      <CommissionerOnlyGate>
        <CommandCenterContent />
      </CommissionerOnlyGate>
    </LscAuthGate>
  );
};

function CommandCenterContent() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const { league, loading, error } = useLeague();
  const [matchCount, setMatchCount] = useState<number | undefined>();
  const [memberCount, setMemberCount] = useState<number | undefined>();
  const [memberDetail, setMemberDetail] = useState<string | undefined>();
  const [candidateCount, setCandidateCount] = useState<number | undefined>();
  const [vodCandidates, setVodCandidates] = useState<LscVodCandidate[]>([]);
  const [vodCandidatesLoading, setVodCandidatesLoading] = useState(false);
  const [vodCandidatesError, setVodCandidatesError] = useState<string | null>(null);
  const [vodSyncReadyDetail, setVodSyncReadyDetail] = useState<string | undefined>();
  const [archiveCount, setArchiveCount] = useState<number | undefined>();

  useEffect(() => {
    if (!leagueId) return;
    let cancelled = false;
    (async () => {
      try {
        const [matches, memberData, archives] = await Promise.all([
          listLscMatches(leagueId, { scope: 'active' }),
          listLscMembers(leagueId),
          listLscArchive(leagueId, { archive_status: 'published' }).catch(() => []),
        ]);
        if (cancelled) return;
        setMatchCount(matches.length);
        setMemberCount(getOverviewMemberCount(memberData.members, memberData.counts));
        setMemberDetail(formatOverviewMemberDetail(memberData.counts));
        setArchiveCount(archives.length);

        setVodCandidatesLoading(true);
        setVodCandidatesError(null);
        try {
          const [candidates, readiness] = await Promise.all([
            listLscVodCandidates(leagueId, { limit: 500 }),
            getLscVodSyncReadiness(leagueId).catch(() => null),
          ]);
          if (!cancelled) {
            setVodCandidates(candidates);
            setCandidateCount(candidates.length);
            if (readiness) {
              setVodSyncReadyDetail(
                `Twitch-ready: ${readiness.eligible_count} / ${readiness.total_current_members} · ${formatDashboardVodSyncDetail(readiness)}`
              );
            }
          }
        } catch {
          if (!cancelled) {
            setVodCandidates([]);
            setCandidateCount(undefined);
            setVodCandidatesError('Could not load review summary.');
          }
        } finally {
          if (!cancelled) setVodCandidatesLoading(false);
        }
      } catch {
        // optional counts
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leagueId]);

  const renderState = getDashboardRenderState({
    loading,
    error,
    hasLeague: Boolean(league),
  });

  if (renderState === 'loading') {
    return <LoadingState message="Loading command center..." />;
  }
  if (renderState === 'error') {
    return <ErrorBanner message={error!} />;
  }
  if (renderState === 'missing') {
    return <ErrorBanner message="League workspace could not be loaded." />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white mb-1">Command Center</h1>
        <p className="text-sm text-secondary-400">
          Your league launch home — setup progress, automation status, review work, and latest activity.
        </p>
      </div>

      <CommissionerCockpit
        league={league!}
        leagueId={leagueId!}
        matchCount={matchCount}
        memberCount={memberCount}
        memberDetail={memberDetail}
        candidateCount={candidateCount}
        vodSyncReadyDetail={vodSyncReadyDetail}
        archiveCount={archiveCount}
        vodCandidates={vodCandidates}
        vodCandidatesLoading={vodCandidatesLoading}
        vodCandidatesError={vodCandidatesError}
        defaultExpanded
        embedded
      />
    </div>
  );
}

export default LscCommandCenterPage;
